import type { Namespaces } from 'bundles/epic/client';
import { ProductVariant } from 'bundles/premium-hub/constants';
import type { Degree } from 'bundles/premium-hub/types';

import fakeBannerImage from 'images/premium-hub/fake-banner-image.jpg';
import fakeDegreeLogo from 'images/premium-hub/fake-university-logo.png';

type FakeDegreeData = Degree & {
  ranking?: string;
  description?: string;
  atAGlance?: {
    timeFrame?: string;
    tuition?: string;
    ranking?: string;
    location?: string;
  };
};

export const pmfTestPages = {
  degreeHub: 'degreeHub',
  compSciHub: 'compSciHub',
  dataSciHub: 'dataSciHub',
} as const;

type PmfTestPage = typeof pmfTestPages[keyof typeof pmfTestPages];

const fakeCsDegree1: FakeDegreeData = {
  id: 'fakeComputerScienceBachelors',
  slug: 'bsc-computer-science-pmf',
  marketingName: 'Bachelor of Science in Computer Science',
  catalogAvailabilityStatus: '',
  name: 'Bachelor of Science in Computer Science',
  productVariant: ProductVariant.BachelorsDegree,
  partners: {
    elements: [
      {
        name: 'Top ranked U.S. university',
        id: '1',
        location: { country: 'US' },
        squareLogo: fakeDegreeLogo,
      },
    ],
  },
  ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  description: 'From a top ranked U.S. university in collaboration with leading technology companies',
  bannerImage: fakeBannerImage,
  atAGlance: {
    timeFrame: '36 – 72 months',
    tuition: '$10,000',
    ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  },
};

const fakeCsDegree2: FakeDegreeData = {
  id: 'fakeComputerScienceMasters',
  slug: 'ms-computer-science-pmf',
  marketingName: 'Master of Science in Computer Science',
  catalogAvailabilityStatus: '',
  name: 'Master of Science in Computer Science',
  productVariant: ProductVariant.MastersDegree,
  partners: {
    elements: [
      {
        name: 'Top ranked U.S. university',
        id: '1',
        location: { country: 'US' },
        squareLogo: fakeDegreeLogo,
      },
    ],
  },
  ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  description: 'From a top ranked U.S. university in collaboration with leading technology companies',
  bannerImage: fakeBannerImage,
  atAGlance: {
    timeFrame: '18 – 36 months',
    tuition: '$10,000',
    ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  },
};

const fakeDataSciDegree1: FakeDegreeData = {
  id: 'fakeDataScienceBachelors',
  slug: 'bsc-data-science-pmf',
  marketingName: 'Bachelor of Science in Data Science',
  catalogAvailabilityStatus: '',
  name: 'Bachelor of Science in Data Science',
  productVariant: ProductVariant.BachelorsDegree,
  partners: {
    elements: [
      {
        name: 'Top ranked U.S. university',
        id: '1',
        location: { country: 'US' },
        squareLogo: fakeDegreeLogo,
      },
    ],
  },
  ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  description: 'From a top ranked U.S. university in collaboration with leading technology companies',
  bannerImage: fakeBannerImage,
  atAGlance: {
    timeFrame: '36 – 72 months',
    tuition: '$10,000',
    ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  },
};

const fakeDataSciDegree2: FakeDegreeData = {
  id: 'fakeDataScienceMasters',
  slug: 'ms-data-science-pmf',
  marketingName: 'Master of Science in Data Science',
  catalogAvailabilityStatus: '',
  name: 'Master of Science in Data Science',
  productVariant: ProductVariant.MastersDegree,
  partners: {
    elements: [
      {
        name: 'Top ranked U.S. university',
        id: '1',
        location: { country: 'US' },
        squareLogo: fakeDegreeLogo,
      },
    ],
  },
  ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  description: 'From a top ranked U.S. university in collaboration with leading technology companies',
  bannerImage: fakeBannerImage,
  atAGlance: {
    timeFrame: '18 – 36 months',
    tuition: '$10,000',
    ranking: 'From a top ranked U.S. university in collaboration with leading technology companies',
  },
};

const variantAndPageToDegree: Record<
  Namespaces['GrowthPremium']['paintedDoorDegree'],
  Record<PmfTestPage, FakeDegreeData>
> = {
  CompSci1: {
    degreeHub: fakeCsDegree1,
    compSciHub: fakeCsDegree1,
    dataSciHub: fakeDataSciDegree1,
  },
  CompSci2: {
    degreeHub: fakeCsDegree2,
    compSciHub: fakeCsDegree2,
    dataSciHub: fakeDataSciDegree2,
  },
  DataSci1: {
    degreeHub: fakeDataSciDegree1,
    compSciHub: fakeCsDegree1,
    dataSciHub: fakeDataSciDegree1,
  },
  DataSci2: {
    degreeHub: fakeDataSciDegree2,
    compSciHub: fakeCsDegree2,
    dataSciHub: fakeDataSciDegree2,
  },
};

export const isFakeDegree = (slug: string): boolean => {
  return [fakeCsDegree1, fakeCsDegree2, fakeDataSciDegree1, fakeDataSciDegree2]
    .map((degree) => degree.slug)
    .includes(slug);
};

export const getFakeDegree = (
  epicExperimentValue: Namespaces['GrowthPremium']['paintedDoorDegree'],
  pmfPage: PmfTestPage
): FakeDegreeData => {
  if (!epicExperimentValue || !variantAndPageToDegree[epicExperimentValue]) {
    switch (pmfPage) {
      case pmfTestPages.degreeHub:
        return fakeCsDegree1;
      case pmfTestPages.compSciHub:
        return fakeCsDegree1;
      case pmfTestPages.dataSciHub:
        return fakeDataSciDegree1;
      default:
        return fakeCsDegree1;
    }
  }
  return variantAndPageToDegree[epicExperimentValue][pmfPage];
};
